import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { getApiBaseUrl } from '../../utils/api';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  PersonAdd as SignupIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { LOGIN_PORTAL_THEMES as THEMES } from '../../theme/loginPortalThemes';
import axios from 'axios';
import { getPublicApiBase } from '../../utils/publicApi';
import { resolveBrandingLogoImageUrl } from '../../utils/publicAssetUrl';
import { PublicBrandingIdentity } from '../../components/public/PublicBrandingIdentity';
import SharedTemplateChooser from '../../components/public/SharedTemplateChooser';
import PublicSiteNav from '../../components/public/PublicSiteNav';
import AuthorizedAccessDisclaimer from '../../components/public/AuthorizedAccessDisclaimer';
import { useAppBranding } from '../../context/AppBrandingContext';
import { DEFAULT_REMEMBER_USERID_DAYS, normalizeRememberUseridDays } from '../../constants/mfaMethods';
import {
  loadRememberedUserid,
  saveRememberedUserid,
  clearRememberedUserid
} from '../../utils/rememberUserid';
import {
  getSharedPageAtmosphere,
  subscribeSharedPageAtmosphere
} from '../../utils/sharedPageAtmosphere';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  /** Marketing landing at `/`; query portal at `/query`. */
  const entryLabel = location.state?.entryLabel;
  const { login } = useAuth();
  const remembered = loadRememberedUserid();
  const [formData, setFormData] = useState({
    email: remembered?.email || '',
    password: ''
  });
  const [rememberMe, setRememberMe] = useState(Boolean(remembered?.email));
  const [rememberUseridDays, setRememberUseridDays] = useState(DEFAULT_REMEMBER_USERID_DAYS);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [theme, setTheme] = useState(() => getSharedPageAtmosphere());
  const [loginBranding, setLoginBranding] = useState({
    logoUrl: null,
    companyName: null,
    companyAddress: null,
    companyCity: null,
    companyZip: null,
    companyWebsite: null,
    companyContactEmail: null,
    companyTagline: null,
    companyNameStyle: null,
    companyTaglineStyle: null
  });

  const currentTheme = THEMES[theme] || THEMES.light;
  const { footerText } = useAppBranding();
  const navBarVariant = ['light', 'aqua', 'orchid'].includes(theme) ? 'light' : 'dark';

  useEffect(() => subscribeSharedPageAtmosphere(setTheme), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = getApiBaseUrl().replace(/\/+$/, '');
        const r = await axios.get(`${base}/settings/login-auth-options`);
        if (!cancelled) {
          setRememberUseridDays(normalizeRememberUseridDays(r.data?.data?.rememberUseridDays));
        }
      } catch {
        if (!cancelled) setRememberUseridDays(DEFAULT_REMEMBER_USERID_DAYS);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async (cacheBust) => {
      try {
        const base = getPublicApiBase().replace(/\/+$/, '');
        const r = await axios.get(`${base}/branding`);
        const d = r.data?.data || {};
        const path = d.portalLogoPath;
        if (cancelled) return;
        setLoginBranding({
          logoUrl: path && String(path).trim() ? resolveBrandingLogoImageUrl(cacheBust ?? path) : null,
          companyName: d.companyName ?? null,
          companyAddress: d.companyAddress ?? null,
          companyCity: d.companyCity ?? null,
          companyZip: d.companyZip ?? null,
          companyWebsite: d.companyWebsite ?? null,
          companyContactEmail: d.companyContactEmail ?? null,
          companyTagline: d.companyTagline ?? null,
          companyNameStyle: d.companyNameStyle ?? null,
          companyTaglineStyle: d.companyTaglineStyle ?? null
        });
      } catch {
        if (!cancelled) {
          setLoginBranding({
            logoUrl: null,
            companyName: null,
            companyAddress: null,
            companyCity: null,
            companyZip: null,
            companyWebsite: null,
            companyContactEmail: null,
            companyTagline: null,
            companyNameStyle: null,
            companyTaglineStyle: null
          });
        }
      }
    };
    load();
    const onBrandingUpdated = () => load(Date.now());
    window.addEventListener('erp4c-branding-updated', onBrandingUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener('erp4c-branding-updated', onBrandingUpdated);
    };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        if (rememberMe) {
          saveRememberedUserid(formData.email, rememberUseridDays);
        } else {
          clearRememberedUserid();
        }
      }

      if (result.success && result.data?.requireOTP) {
        // Navigate to OTP verification
        navigate('/verify-otp', { 
          state: { 
            userId: result.data.userId,
            otpType: result.data.otpType,
            email: formData.email,
            otpIssuer: result.data.otpIssuer,
            mfaMethodLabel: result.data.mfaMethodLabel,
            mfaMethod: result.data.mfaMethod
          }
        });
        return;
      }

      // Success but no OTP required (e.g. OTP disabled) - should not happen with current backend
      if (result.success && result.data?.token) {
        const { token, refreshToken, user } = result.data;
        localStorage.setItem('token', token);
        if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
        window.location.href = '/dashboard';
        return;
      }

      // Unexpected response
      setError(result?.message || 'Login succeeded but invalid response. Please try again.');
    } catch (err) {
      // Show specific error message from server, or friendly fallbacks
      let message = 'Login failed. Please try again.';
      if (err.response?.data?.message) {
        message = err.response.data.message;
      } else if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        message = `Unable to connect to the API at ${getApiBaseUrl()}. Run ./01-port-forward-docker-desktop-k8s-local-2-local-laptop.sh (port 9000 only). Open http://127.0.0.1:9000 — API is at /api on the same host.`;
      } else if (err.response?.status === 401) {
        message = err.response?.data?.message || 'Invalid email or password.';
      } else if (err.response?.status === 403) {
        message = err.response?.data?.message || 'Access denied.';
      } else if (err.response?.status >= 500) {
        message = err.response?.data?.message || 'Server error. Please try again later.';
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: currentTheme.background,
        position: 'relative',
        overflow: 'hidden',
        transition: 'background 0.4s ease-in-out'
      }}
    >
      <PublicSiteNav barVariant={navBarVariant} active="signin" />
      <SharedTemplateChooser
        title="Template chooser"
        subtitle="Same look across Sign In, Register, Browsing, and Landing."
        topOffset={{ xs: 68, sm: 76 }}
      />

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          minHeight: 0
        }}
      >
      {/* Background decorations */}
      <Box
        sx={{
          position: 'absolute',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${currentTheme.decorColor1} 0%, transparent 70%)`,
          top: -100,
          right: -100,
          transition: 'background 0.4s ease-in-out',
          pointerEvents: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${currentTheme.decorColor2} 0%, transparent 70%)`,
          bottom: -50,
          left: -50,
          transition: 'background 0.4s ease-in-out',
          pointerEvents: 'none'
        }}
      />

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: { xs: 3, sm: 4 },
          position: 'relative',
          zIndex: 1
        }}
      >
      <Card
        sx={{
          maxWidth: 420,
          width: '100%',
          backgroundColor: currentTheme.cardBg,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${currentTheme.cardBorder}`,
          animation: 'fadeIn 0.5s ease-out',
          transition: 'all 0.4s ease-in-out',
          boxShadow: theme === 'light' 
            ? '0 8px 32px rgba(99, 102, 241, 0.15)' 
            : '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Branding — same as Settings → Branding (logo + company + tagline) */}
          <Box sx={{ mb: 4 }}>
            <PublicBrandingIdentity
              variant="login"
              logoSrc={loginBranding.logoUrl}
              companyName={loginBranding.companyName}
              companyAddress={loginBranding.companyAddress}
              companyCity={loginBranding.companyCity}
              companyZip={loginBranding.companyZip}
              companyWebsite={loginBranding.companyWebsite}
              companyContactEmail={loginBranding.companyContactEmail}
              companyTagline={loginBranding.companyTagline}
              companyNameStyle={loginBranding.companyNameStyle}
              companyTaglineStyle={loginBranding.companyTaglineStyle}
              loginTextPrimary={currentTheme.textPrimary}
              loginTextSecondary={currentTheme.textSecondary}
            />
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 2.5, color: currentTheme.textSecondary }}>
              {entryLabel
                ? entryLabel === 'Administrator' || entryLabel === 'Sign in' || entryLabel === 'Sign In / Login'
                  ? 'Sign in to your account'
                  : `Sign in to ${String(entryLabel).toLowerCase()}`
                : 'Sign in to your account'}
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }} 
              onClose={() => setError('')}
              role="alert"
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              name="email"
              label="Email Address"
              placeholder="Please enter your email ID"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoFocus
              sx={{ 
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: currentTheme.inputBg,
                  '& fieldset': {
                    borderColor: theme === 'light' ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.15)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(99, 102, 241, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#6366f1',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: currentTheme.textSecondary,
                },
                '& .MuiOutlinedInput-input': {
                  color: currentTheme.textPrimary,
                  '&::placeholder': {
                    color: currentTheme.textSecondary,
                    opacity: 0.7,
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: currentTheme.textSecondary }} />
                  </InputAdornment>
                ),
                }}
              />

            <FormControlLabel
              sx={{ mb: 1.5, ml: 0, alignItems: 'center' }}
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{ color: currentTheme.textSecondary, '&.Mui-checked': { color: '#6366f1' } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: currentTheme.textSecondary }}>
                  Remember my userid for {rememberUseridDays} day{rememberUseridDays === 1 ? '' : 's'}
                </Typography>
              }
            />

            <TextField
              fullWidth
              name="password"
              label="Password"
              placeholder="Please enter your password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: currentTheme.inputBg,
                  '& fieldset': {
                    borderColor: theme === 'light' ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.15)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(99, 102, 241, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#6366f1',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: currentTheme.textSecondary,
                },
                '& .MuiOutlinedInput-input': {
                  color: currentTheme.textPrimary,
                  '&::placeholder': {
                    color: currentTheme.textSecondary,
                    opacity: 0.7,
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: currentTheme.textSecondary }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: currentTheme.textSecondary }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                },
                boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)',
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Signup Link */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              sx={{ color: currentTheme.textSecondary }}
            >
              Don't have an account?{' '}
              <Link 
                to="/signup" 
                style={{ 
                  color: '#8b5cf6', 
                  textDecoration: 'none',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <SignupIcon fontSize="small" />
                Sign Up
              </Link>
            </Typography>
          </Box>

          {/* Forgot Password Link */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              sx={{ color: currentTheme.textSecondary }}
            >
              <Link 
                to="/forgot-password" 
                style={{ 
                  color: currentTheme.textSecondary, 
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Forgot your password?
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
      </Box>

      {/* Legal notice below the centered login card */}
      <Box
        sx={{
          flexShrink: 0,
          width: '100%',
          maxWidth: 720,
          mx: 'auto',
          px: 2,
          pb: 3,
          pt: 0,
          position: 'relative',
          zIndex: 1
        }}
      >
        <AuthorizedAccessDisclaimer
          companyName={loginBranding.companyName}
          textPrimary={currentTheme.textPrimary}
          textSecondary={currentTheme.textSecondary}
          sx={{ mb: 1.5 }}
        />
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            color: currentTheme.textSecondary,
            opacity: 0.7
          }}
        >
          {footerText}
        </Typography>
      </Box>
      </Box>
    </Box>
  );
};

export default Login;
